"use strict";

import Caching from './Caching';
import Session from './Session';
import Price from '../Helpers/Price';
import DomUpdater from '../Helpers/DomUpdater';
import Utils from '../Helpers/Utils';

export default class PageProcessor {

	constructor() 
	{
		this.linkId = '';
		this.loanInformation = null;
		this.isInDebugMode = false;
		this.duration = 0;
		this.pageType = '';
		this.active = false;
		this.displayType = '';
		this.contentService = null;
		this.elementBinding = null;
		this.hasBeenInitialized = false;
		this.cleanedPriceCents = 0;
		this.minimumLoanAmountCents = 200000;
	}

	/**
	 * Run page processor
	 * @type {Function}
	 */
	process(callback = null)
	{
		// When "process" is called, we flush the element cache, since elements might be repainted without page refresh.
		DomUpdater.flushElementCache();
		this.cleanedPriceCents = 0;

		this.initialize();
		if (typeof callback === 'function') callback();
	}

	getWrapperClass()
	{
		return "sparxpres__" + this.pageType;
	}

	getWrapperClassStyle() {
		const customMainColor = this.settings.general.customColor && this.settings.general.customColor.startsWith("#") ? "--sparxpres-main-color:"+this.settings.general.customColor+";" : "";
		const customSliderColor = this.settings.general.sliderCustomColor && this.settings.general.sliderCustomColor.startsWith("#") ? "--sparxpres-slider-bg-color:"+this.settings.general.sliderCustomColor+";" : "";

		return customMainColor + customSliderColor;
	}

	/**
	 * Wrap HTML in a container that allows us to show and hide the content
	 * @param financeContent
	 * @param xpresPayContent
	 * @return
	 */
	wrapHTMLInDisplayContainer(financeContent = '', xpresPayContent = '') {
		let displayContainer = '';
		if (financeContent) {
			displayContainer += '<div class="sparxpres_' + this.pageType + '">' + financeContent + '</div>';
		}

		if (xpresPayContent) {
			displayContainer += '<div class="sparxpres_xprespay">' + xpresPayContent + '</div>';
		}
		return displayContainer;
	}

	/**
	 * Set settings
	 * @param {Object} settings
	 */
	setSettings(settings)
	{
		this.settings = settings;
		return this;
	}

	/**
	 * Set content service
	 * @param {Object} contentService
	 * @return {Object} this
	 */
	setContentService(contentService)
	{
		this.contentService = contentService;
		return this;
	}

	setElementBinding(elementBinding)
	{
		this.elementBinding = elementBinding;
		return this;
	}

	getElementBinding() {
		return this.elementBinding;
	}

	setActive(active)
	{
		this.active = active;
		return this;
	}

	getActive() 
	{
		return this.active;
	}

	/**
	 * Set link ID
	 * @param {String} linkId
	 * @return {Object} this
	 */
	setLinkId(linkId)
	{
		this.linkId = linkId;
		return this;
	}

	/**
	 * Set debug mode
	 * @param debugMode
	 * @returns {PageProcessor}
	 */
	setDebugMode(debugMode) {
		this.isInDebugMode = debugMode;
		return this;
	}

	/**
	 * Set page type
	 * @param {String} pageType
	 * @return {Object} this
	 */
	setPageType(pageType)
	{
		this.pageType = pageType;
		return this;
	}

	/**
	 * Set loan information
	 * @param loanInfo
	 */
	setLoanInformation(loanInfo = {})
	{
		this.loanInformation = loanInfo;
		this.duration = this.duration || loanInfo.defaultPeriod;
		if (!this.displayType) {
			this.displayType = this.getRangeDisplayType(loanInfo);
		}
		return this;
	}

	/**
     * Update data in dom (in case duration has changed)
     * @param {Object} loanDetails
     */
	updateViewData(loanDetails) 
	{
		DomUpdater.updateValues(loanDetails);
	}

	/**
	 * rePaint element
	 * @param {Object} loanInfo
	 */
	rePaintElement(loanInfo = {}) {
		throw new Error("Method 'rePaintElement()' must be implemented.");
	}

	 /**
	 * Get content to attach to dom
	 * @return {Promise}
	 */
	getContent()
	{
		return this.settings.display.displayType === 'inline'
			? this._getContentFromInline().then(data => data)
			: this.contentService.getContent(this.settings.display.displayType, this.displayType)
				.then(data => data)
				.catch(error => Utils.info("Failed to get content: %s", error));
	}

	/**
	 * Get XpresPay html
	 * @return {Promise}
	 */
	getXpresPayPageContent()
    {
		return this.contentService.getContent("simple", "xprespay")
			.then(data => data);
    }

	/**
	 * Get content from session
	 * @return {Promise}
	 */
	_getContentFromInline() {
		return Promise.resolve({
			"html": {
				"finance": this.settings.display.inlineContent,
				"xprespay": "",
			},
			"script": "",
			"style": ""
		});
	}

    /**
     * Process content from remote services (html, css and js)
	 * @param style
	 * @param content
     * @return {Promise}
     */
    attachContent(style = '', content = '')
    {
		let self = this;

        // self.elementBinding.hardRefreshElement("sparxpresContent");
        // if (!content || self.elementBinding.getElement("sparxpresContent")) {
        //     return Promise.reject();
        // }

        return self.attachHTMLToView(
            self.settings.display.insertMethod,
            content,
			style
		);
    }

	addEventListener(eventName, functionToExecute)
	{
		let self = this;
		if (!window._sparxpres.hasOwnProperty('eventHandlers')) {
			window._sparxpres['eventHandlers'] = [];
		}

		let _tmpFunction = function(event) {
            if (self.active) {
                functionToExecute(event);
            }
        };

		window._sparxpres.eventHandlers[eventName] = _tmpFunction;
		window.addEventListener(eventName, _tmpFunction);
	}

	/**
	 *
	 * @param  {String} insertPosition
	 * @param  {String} html
	 * @param  {String} style
	 * @return {Promise}
	 */
	attachHTMLToView(insertPosition, html = '', style = '')
	{
		let self = this;

		self.elementBinding.hardRefreshElement("sparxpresContent");
		self.elementBinding.hardRefreshElement("insertContainer");

		try {
			self.elementBinding.addToElement(
				'insertContainer',
				insertPosition,
				self.getWrapperClass(),
				self.getWrapperClassStyle(),
				html,
				style
			);
			return Promise.resolve();
		} catch (error) {
			return Promise.reject();
		}
	}

	/**
	 * Append custom CSS to body if custom CSS is not empty
	 * @param {String} customCss
	 */
	appendCustomCssToHead(customCss = '') {
		if (customCss && customCss.trim()) {
			let styleElement = document.createElement("style");
			styleElement.innerHTML = customCss;

			document.head.appendChild(styleElement);
		}
	}

	/**
	 * Check if needed data is available
	 */
	readyForAttach() 
	{
		return this.elementBinding.getElement('insertContainer') != null;
	}

	/**
	 * After attachment (HTML)
	 * @param {String} script
	 * @return
	 */
	afterAttachment(script = '')
	{
		this.runScript(script);
	}

	/**
	 * Run script using eval
	 * @param {String} script
	 * @return
	 */
	runScript(script = '')
	{
		if (script && script.trim()) {
			eval(script);
		}
	}

	/**
	 * Set loan calculator service
	 * @param {Object} loanCalculator
	 * @return {Object} this
	 */
	setLoanCalculatorService(loanCalculator)
	{
		this.loanCalculator = loanCalculator;
		return this;
	}

	/**
	 * Set loan information service
	 * @param {Object} loanInformationService
	 * @return {Object} this
	 */
	setLoanInformationService(loanInformationService)
	{
		this.loanInformationService = loanInformationService;
		return this;
	}

	/**
	 * Get cleaned price from dom in cents
	 * @return {Number}
	 */
	getPriceCents(forceUpdate = false)
	{
		if (forceUpdate || this.cleanedPriceCents < 1) {
			if (forceUpdate) this.elementBinding.refreshElement("price");
			this.cleanedPriceCents = Price.cleanPrice(this.elementBinding.getValue("price"), this.isInDebugMode);
		}
		return this.cleanedPriceCents;
	}

	/**
	 * Get loan details
	 * @return {Promise}
	 */
	getLoanDetails(priceCents = 0, duration = 0)
	{
		let self = this;
		let _loanDetails;
		let _duration = duration || self.duration;
		let _priceCents = priceCents || self.getPriceCents();
		let key = "loanDetails_" + Math.ceil(_priceCents / 100) + "_" + _duration;

		if (_priceCents < 1000 || _duration < 1) {
			return Promise.reject();
		}

		if (_loanDetails = Caching.get(key)) {
			self.debug("PageProcessor.getLoanDetails with key=%s, returned from cache...", key);
			if (self.loanInformation.loanId !== _loanDetails.loanId) {
				self.debug("PageProcessor.getLoanDetails, loanId changed, rePaintElement.... (%d!==%d)", self.loanInformation.loanId, _loanDetails.loanId);
				self.getLoanInformation(_priceCents, _duration, true)
					.then(loanInfo => {
						self.rePaintElement(loanInfo);
					});
			}
			return Promise.resolve(_loanDetails);
		}

		return self.loanCalculator.getLoanDetails(_priceCents, _duration)
			.then(loanDetails => {
				if (loanDetails.hasOwnProperty("success")) {
					let loanDetailResponse = {};

					if (loanDetails.success) {
						loanDetailResponse['loanId'] = loanDetails.loanId;
						loanDetailResponse['compliance-text'] = loanDetails.complianceText;
						loanDetailResponse['monthly-payments'] = loanDetails.formattedMonthlyPayments;

						if (self.loanInformation.loanId !== loanDetails.loanId) {
							self.debug("PageProcessor.getLoanDetails, loanId changed, fetch loan information and rePaintElement.... (%d!==%d)", self.loanInformation.loanId, loanDetails.loanId);
							self.getLoanInformation(_priceCents, _duration, true)
								.then(loanInfo => {
									self.rePaintElement(loanInfo);
								});
						}

						self.duration = loanDetails.termsInMonths;
						key = "loanDetails_" + loanDetails.loanAmount + "_" + loanDetails.termsInMonths;
						self.debug("PageProcessor.getLoanDetails, key=%s put into cache...", key);
						Caching.set(key, loanDetailResponse);
					} else {
						loanDetailResponse['compliance-text'] = _priceCents < 2000 ? 'Dit køb kan finansieres fra 2.000 kr.' : 'Det er desværre ikke muligt at tilbyde finansiering på det valgte beløb og løbetid. Prøv med en anden løbetid.';
						loanDetailResponse['monthly-payments'] = 'N/A';
					}

					return Promise.resolve(loanDetailResponse);
				}

				return Promise.reject();
			});
	}

	/**
	 * Get loan information from, session, cache or remote service.
	 * @param {Number} priceCents
	 * @param {Number} duration
	 * @param {Boolean} forceUpdate
	 * @return {Promise}
	 */
	getLoanInformation(priceCents = 0, duration = 0, forceUpdate = false)
	{
		let self = this;
		const sessionKey = "sparxpres-loan-info";

		if (!forceUpdate) {
			let loanInfo = Session.get(sessionKey);
			if (loanInfo) {
				self.debug("PageProcessor.getLoanInformation with key: %s (price: %d), returned from session cache", sessionKey, priceCents);
				self.setLoanInformation(loanInfo);
				return Promise.resolve(loanInfo);
			}
		}

		self.debug("PageProcessor.getLoanInformation from remote");
		return self.loanInformationService.getLoanInformation(priceCents, duration)
			.then(remoteLoanInfo => {
				self.setLoanInformation(remoteLoanInfo);
				Session.set(sessionKey, remoteLoanInfo);
				return remoteLoanInfo;
			});
	}

	/**
	 * Determine display type based on loan period intervals
	 * @param {Object} loanInfo
	 * @return {String}
	 */
	getRangeDisplayType(loanInfo)
	{
		let self = this;
		const loanPeriods = loanInfo.loanPeriods;
		const length = loanPeriods.length;
		const dropdownLimit = self.settings.display.dropdownLimit || 1;
		const unevenPeriodSpread = self.unevenPeriodSpread(loanPeriods);

		if (length === 1) {
			return "plain";
		} else if (length <= dropdownLimit || unevenPeriodSpread) {
			return "dropdown";
		}

		return "slider";
	}

	/**
	 * Determine if periods are uneven
	 * @param {Object} loanPeriods
	 * @return {Boolean}
	 */
	unevenPeriodSpread(loanPeriods)
	{
		let difference;
		loanPeriods.forEach(period => {
			if (!difference) {
				difference = period;
			} else if (difference !== period) {
				return true;
			}
		});

		return false;
	}

	/**
	 * Log a message if in debug mode
	 * @param msg
	 * @param args
	 */
	debug(msg, ...args) {
		Utils.debug(this.isInDebugMode, msg, ...args);
	}

}